<template>
    <sidebar>
        <sidebar-header class="p-0">
            <div
                :class="{
                    'px-2': sidebar.unfoldable,
                    'px-4': !sidebar.unfoldable,
                }"
                class="sidebar-brand-wrapper flex w-full items-center justify-center"
            >
                <sidebar-brand v-if="logoUrl" class="clickable custom flex items-center" @click.stop="$inertia.visit('/')">
                    <div class="flex items-center justify-center">
                        <img
                            :class="{
                                'sidebar-brand__full': !sidebar.unfoldable,
                                'sidebar-brand__narrow': sidebar.unfoldable,
                            }"
                            :src="logoUrl"
                            alt="App logo"
                        />
                    </div>
                </sidebar-brand>
                <sidebar-brand v-else class="clickable flex w-full items-center" @click.stop="$inertia.visit('/')">
                    <div class="curie_logo mb-[49px] flex items-center justify-start pt-3 pb-[11px] text-white">
                        <LogoCurie v-if="!sidebar.unfoldable" :height="38" :width="144" title="Curie Technologies Inc." />
                        <LogoCurieCC v-else :height="32" :width="34" title="Curie Technologies Inc." />
                    </div>
                </sidebar-brand>
            </div>
            <sidebar-toggler />
        </sidebar-header>
        <div class="flex grow flex-col justify-between">
            <sidebar-nav>
                <sidebar-nav-item
                    v-if="hasAnyPermission('asset.update') && $page.props.assets_stats.onboardingIncomplete > 0"
                    :count="$page.props.assets_stats.onboardingIncomplete"
                    routeName="assets.activation"
                    ><template #icon><Tag /></template
                ></sidebar-nav-item>
                <sidebar-nav-item v-if="hasFeature('task_list') && hasAnyPermission(['task.index'])" icon="cil-task" routeName="tasks.index" />
                <sidebar-nav-item v-if="hasAnyPermission('asset.read')" :count="totalAssetsCount" icon="cil-layers" routeName="assets.index">
                    <ul
                        :class="{
                            hidden: !$page.props.activeNavigation['assets.index'],
                        }"
                        class="sidebar-subnav list-none pl-0"
                    >
                        <sidebar-sub-nav-item
                            :active="routeContainsUsageCategory('active')"
                            :count="countAssetsByUsageCategory('active')"
                            :href="
                                route('assets.index', {
                                    usages: ['active'],
                                })
                            "
                            class="block"
                        >
                            {{ trans('assets.sidebar.active') }}
                        </sidebar-sub-nav-item>
                        <sidebar-sub-nav-item
                            :active="routeContainsUsageCategory('not_present')"
                            :count="countAssetsByUsageCategory('not_present')"
                            :href="
                                route('assets.index', {
                                    usages: ['not_present'],
                                })
                            "
                            class="block"
                        >
                            {{ trans('assets.sidebar.not_present') }}
                        </sidebar-sub-nav-item>
                        <sidebar-sub-nav-item
                            :active="routeContainsUsageCategory('spare')"
                            :count="countAssetsByUsageCategory('spare')"
                            :href="
                                route('assets.index', {
                                    usages: ['spare'],
                                })
                            "
                            class="block"
                        >
                            {{ trans('assets.sidebar.spare') }}
                        </sidebar-sub-nav-item>
                        <sidebar-sub-nav-item
                            :active="routeContainsUsageCategory(['decommissioned'])"
                            :count="countAssetsByUsageCategory('decommissioned')"
                            :href="
                                route('assets.index', {
                                    usages: ['decommissioned'],
                                })
                            "
                            class="block"
                        >
                            {{ trans('assets.sidebar.decommissioned') }}
                        </sidebar-sub-nav-item>
                    </ul>
                </sidebar-nav-item>
                <sidebar-nav-item
                    v-if="hasFeature('dashboard') && hasAnyPermission('dashboard.read')"
                    icon="cil-bar-chart"
                    routeName="dashboard.index"
                />
                <sidebar-nav-item
                    v-if="hasFeature('smiths_dashboard') && hasAnyPermission('smiths.read')"
                    icon="cil-screen-desktop"
                    routeName="eds-operations.index"
                />
                <sidebar-nav-item
                    v-if="hasFeature('asset-work-order-management') && hasAnyPermission('workorder.read')"
                    icon="cil-view-column"
                    routeName="workorders.index"
                />
                <li v-if="showReporting" class="nav-item">
                    <a
                        :class="{
                            'c-active': isReportingActive,
                        }"
                        :title="trans('navigation.reporting')"
                        class="nav-link"
                        href="javascript:void(0)"
                        @click.prevent="showReportingSubNav = !showReportingSubNav"
                    >
                        <icon class="nav-icon" name="cil-description" />
                        {{ trans('navigation.reporting') }}
                    </a>
                    <collapse :show="isReportingActive || showReportingSubNav">
                        <ul class="sidebar-subnav list-none pl-0">
                            <sidebar-sub-nav-item
                                v-if="showInoperableAssets"
                                :active="route().current() === 'reporting-light.index'"
                                :href="route('reporting-light.index')"
                                class="block"
                            >
                                {{ trans('navigation.reporting.inoperable_assets') }}
                            </sidebar-sub-nav-item>
                            <sidebar-sub-nav-item
                                v-if="showReferences"
                                :active="route().current() === 'references.index'"
                                :href="route('references.index')"
                            >
                                {{ trans('references.overview.title') }}
                            </sidebar-sub-nav-item>
                            <sidebar-sub-nav-item
                                v-if="hasAnyPermission('activity.read')"
                                :active="route().current() === 'activities.index'"
                                :href="route('activities.index')"
                            >
                                {{ trans('navigation.reporting.activities') }}
                            </sidebar-sub-nav-item>
                            <sidebar-sub-nav-item
                                v-if="hasFeature('task_compliance_report') && hasAnyPermission('report.read')"
                                :active="route().current() === 'reports.testing-compliance'"
                                :href="route('reports.testing-compliance')"
                            >
                                Testing Compliance
                            </sidebar-sub-nav-item>
                            <sidebar-sub-nav-item
                                v-if="hasFeature('incident_log_report') && hasAnyPermission('report.read')"
                                :active="route().current() === 'reports.incident-log'"
                                :href="route('reports.incident-log')"
                            >
                                Incident Log
                            </sidebar-sub-nav-item>
                            <sidebar-sub-nav-item
                                v-if="hasFeature('asset_downtime_summary_report') && hasAnyPermission('report.read')"
                                :active="route().current() === 'reports.downtime-summary'"
                                :href="route('reports.downtime-summary')"
                            >
                                Asset Downtime Summary
                            </sidebar-sub-nav-item>
                        </ul>
                    </collapse>
                </li>
                <sidebar-nav-title v-if="hasAnyPermission(['user.index', 'location.index', 'settings.read', 'settings.update', 'notification.index'])"
                    >{{ trans('navigation.settings') }}
                </sidebar-nav-title>
                <sidebar-nav-item v-if="hasAnyPermission('user.index')" icon="cil-group" routeName="users.index" />
                <sidebar-nav-item v-if="hasAnyPermission('location.index')" icon="cil-airplane-mode" routeName="locations.index" />
                <sidebar-nav-item
                    v-if="hasAnyPermission(['owner.index', 'operator.index', 'maintainer.index'])"
                    icon="cil-sitemap"
                    routeName="stakeholders.index"
                />
                <sidebar-nav-item
                    v-if="hasAnyPermission(['settings.read', 'settings.update', 'notification.index'])"
                    icon="cil-cog"
                    routeName="settings.index"
                />
                <li v-if="supportLink" :title="trans('navigation.support')" class="nav-item mt-8">
                    <a :href="supportLink" class="nav-link flex items-center justify-between" target="_blank">
                        <span class="nav-icon hidden-wide flex items-center leading-5">?</span>
                        <span class="flex w-full items-center justify-between"
                            >{{ trans('navigation.support') }} <icon name="cil-external-link"
                        /></span>
                    </a>
                </li>
            </sidebar-nav>
            <div class="hidden-narrow flex flex-col items-center py-3">
                <optional-link :href="curieTechUrl" class="text-white">
                    <LogoCurie :height="15" title="Curie Technologies Inc." />
                </optional-link>
                <div v-if="version" class="version px-4 text-center">
                    <small :title="versionTooltip" class="text-muted"
                        ><b>v{{ version }}</b>
                        <template v-if="preRelease"> [{{ preRelease }}]</template>
                        <span v-if="commitsAhead > 0" class="text-warning"> (+{{ commitsAhead }})</span>
                    </small>
                </div>
            </div>
        </div>
    </sidebar>
    <div v-if="sidebar.visible" :class="{ show: sidebar.visible }" class="fade sidebar-backdrop" @click="sidebar.close" />
</template>

<script setup>
import SidebarSubNavItem from '@/Components/SidebarSubNavItem.vue';
import OptionalLink from '@/Components/OptionalLink.vue';
import Sidebar from '@/Components/Sidebar.vue';
import SidebarNavItem from '@/Components/SidebarNavItem.vue';
import SidebarNav from '@/Components/SidebarNav.vue';
import SidebarNavTitle from '@/Components/SidebarNavTitle.vue';
import SidebarBrand from '@/Components/SidebarBrand.vue';
import SidebarToggler from '@/Components/SidebarToggler.vue';
import SidebarHeader from '@/Components/SidebarHeader.vue';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useSidebarStore } from '@/stores/sidebar.js';
import { router, usePage } from '@inertiajs/vue3';
import { isEqual } from 'lodash';
import Collapse from '@/Components/Collapse.vue';
import { hasAnyPermission } from '@/mixins/hasAnyPermission.js';
import { hasFeature } from '@/mixins/hasFeature.js';
import LogoCurie from '../../icons/LogoCurie.vue';
import Tag from '../../icons/Tag.vue';
import LogoCurieCC from '../../icons/LogoCurieCC.vue';

const sidebar = useSidebarStore();
const showReportingSubNav = ref(false);
const showReporting = computed(() => showInoperableAssets.value || showActivities.value || showReferences.value);
const isReportingActive = computed(() => {
    return (
        route().current()?.startsWith('reporting-light.') ||
        route().current()?.startsWith('activities.') ||
        route().current()?.startsWith('references.') ||
        route().current()?.startsWith('reports.')
    );
});
const showInoperableAssets = computed(() => hasFeature('reporting-light') && hasAnyPermission('report.read'));
const showActivities = computed(() => hasAnyPermission('activity.read'));
const showReferences = computed(() => hasFeature('test_record') && hasAnyPermission('reference.read'));

const $page = usePage();
const supportLink = computed(() => $page.props.supportLink);
const logoUrl = computed(() => $page.props.settings.big_logo_url);
const version = computed(() => $page.props.version?.version_core);
const rawVersion = computed(() => $page.props.version?.semver);
const commit = computed(() => $page.props.version?.commit_hash);
const preRelease = computed(() => $page.props.version?.pre_release);
const commitsAhead = computed(() => $page.props.version?.commits_ahead ?? 0);
const versionTooltip = computed(() => {
    const parts = [rawVersion.value, commit.value].filter(Boolean);
    if (commitsAhead.value > 0) {
        parts.push(`${commitsAhead.value} commit(s) ahead`);
    }
    return parts.join(' | ');
});
const totalAssetsCount = computed(() =>
    Object.keys($page.props.assets_stats)
        .filter((key) => key !== 'onboardingIncomplete')
        .reduce((a, b) => a + $page.props.assets_stats[b], 0),
);
const curieTechUrl = computed(() => $page.props.curie_tech_url);

// methods: {
function countAssetsByUsageCategory(usageCategory) {
    return $page.props.assets_stats[usageCategory] || 0;
}

function routeContainsUsageCategory(usageCategory) {
    if (route().current() !== 'assets.index') {
        return false;
    }

    const params = route().params;

    // Wrap in array if necessary
    usageCategory = Array.isArray(usageCategory) ? usageCategory : [usageCategory];

    return isEqual(params.usages, usageCategory);
}

function closeSidebarOnSmallScreens() {
    if (document.body.clientWidth < 992) {
        sidebar.close();
    }
}

let removeEventListener;

onMounted(() => {
    removeEventListener = router.on('start', closeSidebarOnSmallScreens);
});

onUnmounted(() => {
    removeEventListener && removeEventListener();
});
</script>
